import React, { useEffect, useRef, useState } from 'react';
import {
    faArrowDownToLine,
    faArrowUpRightFromSquare,
    faArrowUpToLine,
    faChevronLeft,
    faChevronRight,
    faMinus,
    faPlus,
    faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import * as Dialog from '@radix-ui/react-dialog';

import classnames from '../../../../core/utils/classnames.tsx';
import { withIcon } from '../../../../core/components/Icon.tsx';
import InputDataPreview from './InputDataPreview.tsx';
import { useTranslation } from 'react-i18next';

interface IFile {
    name: string;
    url: string;
    type: string;
}

const DownloadIcon = withIcon(faArrowDownToLine);
const ExternalLinkIcon = withIcon(faArrowUpRightFromSquare);
const ProcessIcon = withIcon(faArrowUpToLine);
const CloseIcon = withIcon(faXmark);
const ChevronRightIcon = withIcon(faChevronRight);
const ChevronLeftIcon = withIcon(faChevronLeft);
const ZoomInIcon = withIcon(faPlus);
const ZoomOutIcon = withIcon(faMinus);

const ToolbarButton = ({ className, children, ...props }: React.ComponentPropsWithRef<'button'>) => (
    <button
        className={classnames(
            'w-8 h-8 flex items-center justify-center hover:bg-surface-white/20 rounded transition-colors',
            className
        )}
        {...props}
    >
        {children}
    </button>
);

const NavigationButton = ({ className, disabled, children, ...props }: React.ComponentPropsWithRef<'button'>) => (
    <button
        className={classnames(
            'w-8 h-8 flex items-center justify-center bg-surface-black  text-content-white rounded transition-colors top-1/2 absolute -translate-y-1/2',
            disabled ? 'opacity-30 cursor-not-allowed' : 'hover:bg-[#333333]',
            className
        )}
        disabled={disabled}
        {...props}
    >
        {children}
    </button>
);

const NotAvailableCard = ({ className }: React.ComponentPropsWithRef<'div'>) => {
    const { t } = useTranslation('assistance');
    return (
        <div className="flex flex-col items-center justify-center px-12 p-8 max-w-[24rem] rounded-lg w-full bg-surface-black text-content-white">
            <div className="text-sm font-semibold text-center">{t('attachments.absentFileTitle')}</div>
            <div className="mt-2 text-sm text-center">{t('attachments.absentFileDescription')}</div>
        </div>
    );
};

const ZoomControlButton = ({ children, className, disabled, ...props }: any) => (
    <button
        className={classnames(
            'flex text-content-white justify-center items-center h-8 w-8 rounded bg-surface-black hover:bg-surface-strong transition-colors duration-200',
            disabled ? 'opacity-30 cursor-not-allowed' : 'hover:bg-[#333333]',
            className
        )}
        disabled={disabled}
        {...props}
    >
        {children}
    </button>
);

const ZoomControls = ({ zoom, setZoom }: { zoom: number; setZoom: (zoom: number) => void }) => {
    const maxZoom = 5;
    const minZoom = 0.5;
    const zoomFactor = 0.1;

    const zoomInOut = (factor) => {
        setZoom(Math.max(minZoom, Math.min(maxZoom, zoom + factor)));
    };

    return (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 rounded-lg bg-surface-black shadow-lg flex gap-1 p-1">
            <ZoomControlButton onClick={() => zoomInOut(-zoomFactor)} disabled={zoom <= minZoom}>
                <ZoomOutIcon />
            </ZoomControlButton>
            <ZoomControlButton className="px-2 h-8 w-auto text-xs font-medium" onClick={() => setZoom(1)}>
                {Math.round(zoom * 100)}%
            </ZoomControlButton>
            <ZoomControlButton onClick={() => zoomInOut(zoomFactor)} disabled={zoom >= maxZoom}>
                <ZoomInIcon />
            </ZoomControlButton>
        </div>
    );
};

const FilePreview = ({ file, zoom }: { file: IFile; zoom: number }) => {
    const [mimeType, mimeSubtype] = file?.type ? file.type.split('/') : [undefined, undefined];
    const isImage = mimeType === 'image';
    const isPdf = mimeType === 'application' && mimeSubtype === 'pdf';

    const [imageWidth, setImageWidth] = useState<number | null>(null);
    const innerRef = useRef<HTMLImageElement>(null);
    useEffect(() => {
        if (isImage && innerRef.current) {
            setImageWidth(innerRef.current.naturalWidth * zoom);
        }
    }, [isImage, zoom]);

    return (
        <InputDataPreview
            className={classnames('min-h-auto max-w-full overflow-auto max-h-full min-h-0 h-full')}
            innerProps={{
                ref: innerRef,
                style: {
                    width: isImage ? (imageWidth === 0 ? 'auto' : `${imageWidth}px`) : undefined,
                    maxWidth: 'none',
                    display: isImage ? 'block' : 'flex',
                },
            }}
            data={{
                ...file,
                url: isPdf ? `${file.url}#toolbar=0&zoom=${Math.round(zoom * 100)}` : file.url,
                __typename: 'File',
            }}
        />
    );
};

const FileSourceLabel = ({ isSource, sourcePart }: { isSource: boolean; sourcePart?: any }) => {
    const { t } = useTranslation('assistance');

    if (isSource) {
        return (
            <span className="p-2 py-1 leading-8 bg-surface-brand-weak rounded text-content-brand-strong text-xs leading-none font-medium border-line-brand-weak border">
                {t('attachments.documentSource')}
            </span>
        );
    }

    return null;
};

const FileActionButtons = ({
    isSource,
    sourcePart,
    activeFile,
    onProcessDocumentSelect,
    onGoToDocumentClick,
    onClose,
}: {
    isSource: boolean;
    sourcePart?: any;
    activeFile: IFile | null;
    onProcessDocumentSelect?: (file: IFile) => void;
    onGoToDocumentClick?: (recordId: string) => void;
    onClose?: () => void;
}) => {
    const { t } = useTranslation('assistance');

    if (!sourcePart) {
        return null;
    }

    if (sourcePart?.recordId && !isSource && onGoToDocumentClick) {
        return (
            <>
                <button
                    onClick={() => {
                        if (onClose) {
                            onClose();
                        }
                        onGoToDocumentClick(sourcePart.recordId);
                    }}
                    className="px-3 h-8 leading-8 hover:bg-surface-white/20 text-content-white text-sm font-medium flex items-center gap-1.5"
                >
                    {t('attachments.goToDocument')} <ExternalLinkIcon />
                </button>
                <div className="h-6 border-r mt-1 mx-3 border-solid !border-line-weak" />
            </>
        );
    }

    if (activeFile && !isSource && onProcessDocumentSelect) {
        return (
            <>
                <button
                    onClick={() => {
                        if (onClose) {
                            onClose();
                        }
                        onProcessDocumentSelect(activeFile);
                    }}
                    className="px-3 h-8 leading-8 hover:bg-surface-white/20 text-content-white text-sm font-medium flex items-center gap-1.5"
                >
                    <ProcessIcon /> {t('attachments.processDocument')}
                </button>
                <div className="h-6 border-r mt-1 mx-3 border-solid !border-line-weak" />
            </>
        );
    }

    return null;
};

const FileInspectorModal = ({
    className,
    files,
    attachmentsWithSourceParts,
    onClose,
    initialFile,
    onProcessDocumentSelect,
    onGoToDocumentClick,
    ...props
}: React.ComponentPropsWithRef<'div'> & {
    initialFile?: IFile;
    files: IFile[];
    onClose?: () => void;
    attachmentsWithSourceParts?: { isSource: boolean; file: any; sourcePart?: any }[];
    onProcessDocumentSelect?: (file: IFile) => void;
    onGoToDocumentClick?: (recordId: string) => void;
}) => {
    const [zoom, setZoom] = useState(1);

    const [activeFile, setActiveFile] = useState<IFile | null>(null);
    const [mimeType, mimeSubtype] = activeFile?.type ? activeFile.type.split('/') : [undefined, undefined];
    const isImage = mimeType === 'image';
    const isPdf = mimeType === 'application' && mimeSubtype === 'pdf';
    const isOther = !isImage && !isPdf;

    // Get the sourcePart and isSource for the currently active file
    // Match by file name since file objects may not be the same reference
    const activeAttachmentData = attachmentsWithSourceParts?.find((a) => a.file?.name === activeFile?.name);
    const isSource = activeAttachmentData?.isSource || false;
    const sourcePart = activeAttachmentData?.sourcePart;

    const handleOpenChange = (open: boolean) => {
        setZoom(1);
        if (!open && onClose) {
            onClose();
        }
    };

    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = activeFile?.url || '';
        link.download = activeFile?.name || 'download';
        link.target = '_blank';
        link.click();
    };

    const handleNextFile = () => {
        const currentIndex = files.indexOf(activeFile!);
        setZoom(1);
        if (currentIndex < files.length - 1) {
            setActiveFile(files[currentIndex + 1]);
        } else {
            setActiveFile(files[0]); // Loop back to the first file
        }
    };

    const handlePreviousFile = () => {
        const currentIndex = files.indexOf(activeFile!);
        setZoom(1);
        if (currentIndex > 0) {
            setActiveFile(files[currentIndex - 1]);
        } else {
            setActiveFile(files[files.length - 1]); // Loop back to the last file
        }
    };

    useEffect(() => {
        if (initialFile) {
            setActiveFile(initialFile ? files.find((file) => file.name === initialFile.name) || files[0] : files[0]);
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
    }, [files, initialFile]);

    return (
        <Dialog.Root open={Boolean(initialFile)} onOpenChange={handleOpenChange}>
            <Dialog.Portal>
                <Dialog.Overlay
                    className={classnames('fixed w-full h-full inset-0 z-[1000000] bg-surface-black/80', className)}
                    {...props}
                >
                    <Dialog.Content className="flex flex-col w-full h-full">
                        <div className="flex justify-between items-center px-6 py-4 w-full bg-surface-black text-content-white">
                            <div className="flex items-center gap-3">
                                <Dialog.Title className="text-base font-medium">{activeFile?.name}</Dialog.Title>
                                <FileSourceLabel isSource={isSource} sourcePart={sourcePart} />
                            </div>

                            <div className="flex items-center gap-1">
                                <FileActionButtons
                                    isSource={isSource}
                                    sourcePart={sourcePart}
                                    activeFile={activeFile}
                                    onProcessDocumentSelect={onProcessDocumentSelect}
                                    onGoToDocumentClick={onGoToDocumentClick}
                                    onClose={onClose}
                                />
                                <ToolbarButton onClick={handleDownload}>
                                    <DownloadIcon />
                                </ToolbarButton>
                                <ToolbarButton onClick={() => handleOpenChange(false)}>
                                    <CloseIcon />
                                </ToolbarButton>
                            </div>
                        </div>

                        {activeFile && (
                            <div
                                className={classnames(
                                    'flex justify-center relative items-center flex-1 w-full min-h-0 py-10 px-20 pointer-events-auto'
                                )}
                            >
                                <div
                                    className={classnames(
                                        'flex flex-col max-w-full w-full flex-none h-full max-h-full min-h-0 rounded-xl z-[21000]',
                                        isImage && 'w-auto h-auto',
                                        isOther && 'h-auto w-auto'
                                    )}
                                >
                                    {!isOther ? (
                                        <>
                                            <FilePreview
                                                file={activeFile}
                                                zoom={zoom}
                                                key={`${activeFile.name}-${zoom}`}
                                            />
                                            <ZoomControls zoom={zoom} setZoom={setZoom} />
                                        </>
                                    ) : (
                                        <NotAvailableCard />
                                    )}
                                </div>

                                {files.length > 1 && (
                                    <>
                                        <NavigationButton className="left-4" onClick={handlePreviousFile}>
                                            <ChevronLeftIcon />
                                        </NavigationButton>

                                        <NavigationButton className="right-4" onClick={handleNextFile}>
                                            <ChevronRightIcon />
                                        </NavigationButton>
                                    </>
                                )}
                            </div>
                        )}
                    </Dialog.Content>
                </Dialog.Overlay>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

export default FileInspectorModal;
